import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import TipTapImage from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, Heading1, Heading2, Heading3,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Quote, Minus, Link2, Image, Undo, Redo, Type
} from 'lucide-react';

function ToolbarButton({ onClick, active, title, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`p-1.5 rounded-lg transition-colors ${
        active ? 'bg-amber-500/20 text-amber-400' : 'text-white/40 hover:text-white hover:bg-white/5'
      }`}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }) {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt('Enter image URL:');
    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  };

  const S = 16;

  return (
    <div className="flex flex-wrap gap-0.5 p-2 border-b border-white/5 bg-white/[0.02]" data-testid="rte-toolbar">
      <ToolbarButton onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive('paragraph')} title="Paragraph">
        <Type size={S} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Heading 1">
        <Heading1 size={S} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Heading 2">
        <Heading2 size={S} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Heading 3">
        <Heading3 size={S} />
      </ToolbarButton>

      <div className="w-px h-6 bg-white/10 mx-1 self-center" />

      <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
        <Bold size={S} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
        <Italic size={S} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
        <UnderlineIcon size={S} />
      </ToolbarButton>

      <div className="w-px h-6 bg-white/10 mx-1 self-center" />

      <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet List">
        <List size={S} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered List">
        <ListOrdered size={S} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Quote">
        <Quote size={S} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider">
        <Minus size={S} />
      </ToolbarButton>

      <div className="w-px h-6 bg-white/10 mx-1 self-center" />

      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Align Left">
        <AlignLeft size={S} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Align Center">
        <AlignCenter size={S} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Align Right">
        <AlignRight size={S} />
      </ToolbarButton>

      <div className="w-px h-6 bg-white/10 mx-1 self-center" />

      <ToolbarButton onClick={addLink} active={editor.isActive('link')} title="Insert Link">
        <Link2 size={S} />
      </ToolbarButton>
      <ToolbarButton onClick={addImage} title="Insert Image">
        <Image size={S} />
      </ToolbarButton>

      <div className="w-px h-6 bg-white/10 mx-1 self-center" />

      <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Undo">
        <Undo size={S} />
      </ToolbarButton>
      <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Redo">
        <Redo size={S} />
      </ToolbarButton>
    </div>
  );
}

export default function RichTextEditor({ content = '', onChange, placeholder = 'Start writing...' }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable built-in extensions that we're configuring separately
        link: false,
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-amber-400 underline' } }),
      TipTapImage.configure({ inline: false, allowBase64: true }),
      Placeholder.configure({ placeholder }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-invert prose-sm max-w-none p-4 min-h-[200px] focus:outline-none',
      },
    },
  });

  return (
    <div className="bg-[#0d0f17] border border-white/10 rounded-xl overflow-hidden" data-testid="rich-text-editor">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
      <style>{`
        .ProseMirror h1 { font-size: 1.5rem; font-weight: 600; margin: 0.75rem 0 0.5rem; color: #fff; }
        .ProseMirror h2 { font-size: 1.25rem; font-weight: 600; margin: 0.6rem 0 0.4rem; color: #e2e8f0; }
        .ProseMirror h3 { font-size: 1.1rem; font-weight: 600; margin: 0.5rem 0 0.3rem; color: #cbd5e1; }
        .ProseMirror p { color: #94a3b8; margin: 0.3rem 0; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; color: #94a3b8; }
        .ProseMirror blockquote { border-left: 3px solid #f59e0b40; padding-left: 1rem; margin: 0.5rem 0; color: #94a3b8; font-style: italic; }
        .ProseMirror hr { border-color: #ffffff10; margin: 1rem 0; }
        .ProseMirror img { max-width: 100%; border-radius: 0.5rem; margin: 0.5rem 0; }
        .ProseMirror a { color: #f59e0b; text-decoration: underline; }
        .ProseMirror p.is-editor-empty:first-child::before { content: attr(data-placeholder); color: #475569; pointer-events: none; float: left; height: 0; }
      `}</style>
    </div>
  );
}
